'use client'

import Footer from '@/components/Footer'
import WhitelabelCTA from '@/components/services/WhitelabelCTA'
import ZohoHero from '@/components/zoho/ZohoHero'
import ZohoPlans from '@/components/zoho/ZohoPlans'
import ZohoStages from '@/components/zoho/ZohoStages'
import ZohoAuditBuyButton from '@/components/zoho/ZohoAuditBuyButton'
import { ZOHO_FAQS, ZOHO_SYMPTOMS } from '@/lib/zoho-rescue-data'
import { WarnIcon } from '@/components/zoho/icons'

const ENGAGEMENT_STANDARDS = [
  'Weekly check-ins',
  'MSA and SOW on every engagement',
  'Mutual NDA on request',
  'COI on request',
  'Your data stays in your systems',
  'No licenses resold, no cut of your subscription',
  'US-based, no subcontractors, no offshore',
  'On-site availability',
] as const

export default function ZohoRescuePage() {
  return (
    <div className="bg-white text-slate-900">
      <div className="bg-white lg:px-16">
        <ZohoHero />
      </div>

      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#13479c]">Sound familiar?</p>
        <h2 className="mt-3 text-[32px] font-extrabold leading-[1.15] tracking-[-0.035em] text-[#0a2b5c] sm:text-[42px]">
          If any of this is true, <em className="font-normal italic">your Zoho has drifted</em>
        </h2>
        <ul className="mt-8 space-y-3">
          {ZOHO_SYMPTOMS.map((item) => (
            <li key={item} className="flex items-start gap-3 text-[16px] leading-[1.5] text-slate-700">
              <WarnIcon />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section id="plan" className="scroll-mt-24 mx-auto max-w-4xl px-4 pb-6 text-center sm:px-6 lg:px-8">
        <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#13479c]">How I Work</p>
        <h2 className="mt-3 text-[32px] font-extrabold leading-[1.15] tracking-[-0.035em] text-[#0a2b5c] sm:text-[42px]">
          Six stages, in this order
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-[16.5px] leading-[1.6] text-slate-600">
        I scope it, I build it, I train your team on it. No account manager between us.
        </p>
      </section>

      <ZohoStages />

      <ZohoPlans />

      <section className="relative isolate bg-[#071631] pt-10 pb-0">
        <div className="absolute inset-0 -z-10 overflow-hidden" aria-hidden={true}>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: "url('/images/homepage/cta.avif')" }}
          />
          <div className="absolute inset-0 bg-[#2b2c48]/25" />
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="pb-16">
            <div className="mb-10 text-center">
              <h4 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-100/60">
                Engagement standards
              </h4>
              <div className="mt-4 flex flex-wrap justify-center gap-2.5">
                {ENGAGEMENT_STANDARDS.map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-white/15 bg-white/[0.07] px-4 py-2 text-[13px] font-medium leading-[1.45] text-blue-50/85"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <WhitelabelCTA
              embedded
              hideStats
              heading="Send me your setup"
              buttonText="Send me your setup"
              formSource="zoho-rescue-lp"
              serviceName="Zoho CRM Rescue"
              focusAreaPlaceholder="Zoho One, 18 users, CRM plus Desk and Books. Pipeline stages are a mess and Google Ads numbers don't match anything."
            />
          </div>
        </div>
      </section>

      <section id="faq" className="scroll-mt-24 mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#13479c]">FAQ</p>
        <h2 className="mt-3 text-[32px] font-extrabold leading-[1.15] tracking-[-0.035em] text-[#0a2b5c] sm:text-[42px]">
          Still deciding
        </h2>
        <div className="mt-8 divide-y divide-slate-200 border-t border-slate-200">
          {ZOHO_FAQS.map((faq) => (
            <div key={faq.question} className="py-5">
              <h3 className="text-[17px] font-semibold text-[#0a2b5c]">{faq.question}</h3>
              <p className="mt-3 text-[15px] leading-[1.6] text-slate-600">{faq.answer}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <ZohoAuditBuyButton
            location="zoho_faq"
            className="inline-flex h-14 items-center justify-center rounded-full bg-[#13479c] px-9 text-[16px] font-bold text-white shadow-[0_12px_28px_rgba(19,71,156,0.26)] transition-colors hover:bg-[#0f3a7a] disabled:opacity-60"
          />
        </div>
      </section>

      <Footer />
    </div>
  )
}
